import { createContext, useContext } from 'react';

/** The Cognito tokens a sign-in or refresh resolves to. `expiresAt` is epoch milliseconds of the access token. */
export interface CognitoTokens {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
}

export type AuthErrorCode =
  | 'NotAuthorized'
  | 'UserNotConfirmed'
  | 'UsernameExists'
  | 'CodeMismatch'
  | 'ExpiredCode'
  | 'InvalidPassword'
  | 'SignUpNotAllowed'
  | 'LimitExceeded'
  | 'GoogleSignInFailed'
  | 'Unknown';

export class AuthError extends Error {
  readonly code: AuthErrorCode;

  constructor(code: AuthErrorCode, message?: string) {
    super(message ?? code);
    this.name = 'AuthError';
    this.code = code;
  }
}

/** Talks to Cognito's user-pool API directly, over SRP for email/password (QZ-D28). */
export interface CognitoAuthClient {
  signIn(email: string, password: string): Promise<CognitoTokens>;
  signUp(email: string, password: string): Promise<void>;
  confirmSignUp(email: string, code: string): Promise<void>;
  resendConfirmationCode(email: string): Promise<void>;
  forgotPassword(email: string): Promise<void>;
  confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void>;
  startGoogleSignIn(): Promise<void>;
  completeGoogleSignIn(callbackUrl: string): Promise<CognitoTokens>;
}

/** Keeps the access token in memory and the refresh token behind the API's session cookie (QZ-D29). */
export interface SessionManager {
  start(tokens: CognitoTokens): Promise<void>;
  restore(): Promise<void>;
  getAccessToken(): Promise<string | undefined>;
  signOut(): Promise<void>;
}

export interface AuthServices {
  cognito: CognitoAuthClient;
  session: SessionManager;
}

export const AuthServicesContext = createContext<AuthServices | null>(null);

export const useAuthServices = (): AuthServices => {
  const services = useContext(AuthServicesContext);
  if (!services) {
    throw new Error('useAuthServices must be used inside an AuthServicesContext provider');
  }
  return services;
};
